import {HttpErrorResponse} from '@angular/common/http';
import {Component, inject, signal} from '@angular/core';
import {FormGroup, ReactiveFormsModule} from '@angular/forms';
import {MatButton} from '@angular/material/button';
import {MatIcon} from '@angular/material/icon';
import {MatSnackBar} from '@angular/material/snack-bar';
import {ActivatedRoute, Router, RouterLink} from '@angular/router';
import {createFormGroup, createFormlyOptions} from '@app/resource-explorer/resource.form.config';
import {ProjectCreate, ProjectResponse, ProjectsService} from '@backend';
import {prepareFormPayload} from '@forms/utils/form-utils';
import {ValidationErrorHandler} from '@forms/validationerrorhandler.service';
import {IndexService} from '@middleware';
import {FormlyFieldConfig, FormlyFormOptions, FormlyModule} from '@ngx-formly/core';
import {buildProjectCreateFields} from '@project/project.form.config';
import {mapFormModelToProjectCreate, projectResponseToProjectCreate} from '@project/project.form.mappers';
import {AttestationService} from '@services/attestation/attestation.service';
import {SectionHeaderActions} from '@shared/components/section-header-actions/section-header-actions';
import {firstValueFrom} from 'rxjs';

export const PROJECT_PII_ATTESTATION =
  'I confirm that the text entered here in Project Details ' +
  'does not contain sensitive data (such as Personally Identifiable Information).';

@Component({
  selector: 'app-project-details',
  imports: [FormlyModule, MatButton, MatIcon, ReactiveFormsModule, RouterLink],
  templateUrl: './project-details.component.html',
})
export class ProjectDetailsComponent implements SectionHeaderActions {
  private readonly route = inject(ActivatedRoute);
  form: FormGroup = createFormGroup();
  options: FormlyFormOptions = createFormlyOptions();
  formFields: FormlyFieldConfig[] = [];
  model: ProjectCreate;
  submitting = false;
  public readonly project = signal<ProjectResponse | null>(null);
  private projectsService!: ProjectsService;

  constructor(
    private readonly snackbarService: MatSnackBar,
    private readonly indexService: IndexService,
    private readonly router: Router,
    private readonly attestationService: AttestationService,
    private readonly validationErrorHandler: ValidationErrorHandler,
  ) {
    this.route.data.subscribe(d => {
      const projectData = d['projectData'];
      if (!projectData?.privateProject) return;
      const {privateProject, projectsService} = projectData;
      this.project.set(privateProject);
      this.model = projectResponseToProjectCreate(privateProject);
      this.form.patchValue(this.model);
      const orgName = privateProject.storage_organization?.name ?? '';
      this.formFields = buildProjectCreateFields(orgName);
      this.projectsService = projectsService;
    });
  }

  public sectionHeaderActions = signal([
    {
      target: '/help/private-data-enclave/edit-private-project-details',
      targetTab: '_blank',
      caption: 'Help',
      color: 'var(--mat-sys-primary)',
      icon: 'help_outline',
    },
  ]);

  async submit(event: Event): Promise<void> {
    event.preventDefault();
    if (this.form.status == 'PENDING') {
      this.snackbarService.open("We're making sure everything's alright, try again in a few seconds.", undefined, {
        duration: 5000,
      });
      return;
    } else if (!this.form.valid) {
      this.validationErrorHandler.handleError(this.formFields);
      return;
    }
    if (!(await this.attestationService.confirmWithCancel(PROJECT_PII_ATTESTATION))) return;
    try {
      this.submitting = true;
      const sanitizedForm = await prepareFormPayload(this.model, this.formFields);
      const payload = mapFormModelToProjectCreate(sanitizedForm);

      await firstValueFrom(this.projectsService.updateProject(this.project().id, payload));

      // For project edit, use the existing ID and the project's organization to sync the project:
      await firstValueFrom(
        this.indexService.syncProjectInIndex(this.project().id, {
          storage_organization: this.project().storage_organization.name,
        }),
      );

      this.form.markAsPristine();
      (this as any).__skipUnsavedGuard = true;

      await this.router.navigate(['/private-data-enclave/project/view', this.project().id]);
    } catch (e) {
      this.validationErrorHandler.handleError(this.formFields, <HttpErrorResponse>e);
    } finally {
      this.submitting = false;
    }
  }
}
